import rangeJson from '../data/batteryRanges.json'
import { paybackYearsWithDegradation, type CapacitySweepRow } from './batterySizing'

/**
 * How far a short history's savings estimate can land from the household's real
 * year, as a range around the estimate.
 *
 * The table is calibrated on households that do have a full year
 * (`python/battery_range_calibration`): for each, the report's estimate from 1,
 * 3 and 6 months of history against what its full 12 months give for the same
 * battery. The spread of real ÷ estimate, per season of the report date,
 * scenario and how much of the year the window covered, becomes a pair of
 * multipliers per level: the conformal order statistics that keep the promised
 * share for a household the calibration has not seen. Both tables (about 1,000
 * households, savings before standby) were checked on 47 addresses kept out of the
 * calibration. Dynamic contract: the 80% range caught 83% of their windows, the 50%
 * range 52%. Fixed contract (Zelfverbruik vast, at the default EUR 0.26 / 0.04):
 * 78% and 52%.
 *
 * It fits only the calculation it was calibrated on (see `method` in the JSON):
 * recalibrate when that changes.
 */

/**
 * What a group's multipliers scale. `net`: the savings as shown, after the battery's
 * standby use (the first, dynamic-contract calibration). `gross`: the savings before
 * it, with the standby taken off afterwards. A short history errs in the energy the
 * battery moves, not in its fixed standby, so a gross range holds across tariffs:
 * with a thin margin per stored kWh (a high return price) the standby eats most of
 * the net figure, and a range on the net figure stops catching 8 in 10.
 */
export type RangeBasis = 'net' | 'gross'

export interface RangeTable {
  method: Record<string, unknown>
  levels: number[]
  /** Coverage bin edges on min(weightA, weightI): the less covered stream. */
  edges: number[]
  /** Below this an estimate is too small to scale into a range. */
  min_savings_eur: number
  groups: Array<{
    battery_type: string
    scenario_id: string
    season: string
    bin: number
    ranges: Record<string, [number, number]>
    /** Absent in tables from before the basis existed: those are `net`. */
    basis?: RangeBasis
  }>
}

/** Pairs come in as number[] from JSON; tests/batteryRanges.test.ts checks the shape. */
export const BATTERY_RANGES = rangeJson as unknown as RangeTable

/** The level the report shows: 8 in 10 households land inside. */
export const SHOWN_RANGE_LEVEL = 0.8

/** Pooled over battery types, scenarios or seasons: the fallback. */
const ANY = 'any'

export type RangeSeason = 'winter' | 'spring' | 'summer' | 'autumn'
const SEASONS: RangeSeason[] = ['winter', 'spring', 'summer', 'autumn']

/**
 * Meteorological season of the report date in Dutch time: Dec–Feb winter,
 * Mar–May spring, Jun–Aug summer, Sep–Nov autumn. Which months a window measured,
 * and which the report filled in, matters as much as how many.
 */
export function rangeSeason(reportDate: Date): RangeSeason {
  const month = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Amsterdam', month: 'numeric' }).format(
      reportDate
    )
  )
  return SEASONS[Math.floor((month % 12) / 3)]!
}

/** The coverage bin of a window: by the less covered of its import and feed-in. */
export function rangeBin(weightA: number, weightI: number, edges: number[]): number {
  const covered = Math.min(weightA, weightI)
  for (let k = 0; k < edges.length - 1; k++) {
    if (covered < edges[k + 1]!) return k
  }
  return edges.length - 2
}

const groupKey = (batteryType: string, scenarioId: string, season: string, bin: number) =>
  `${batteryType}|${scenarioId}|${season}|${bin}`

interface IndexedGroup {
  ranges: Record<string, [number, number]>
  basis: RangeBasis
}

const tableIndexes = new WeakMap<
  RangeTable,
  { groups: Map<string, IndexedGroup>; scenarios: Set<string> }
>()

function indexOf(table: RangeTable) {
  let index = tableIndexes.get(table)
  if (!index) {
    index = { groups: new Map(), scenarios: new Set() }
    for (const g of table.groups) {
      index.groups.set(groupKey(g.battery_type, g.scenario_id, g.season, g.bin), {
        ranges: g.ranges,
        basis: g.basis ?? 'net',
      })
      index.scenarios.add(g.scenario_id)
    }
    tableIndexes.set(table, index)
  }
  return index
}

interface RangeLookup {
  batteryType: string
  scenarioId: string
  weightA: number
  weightI: number
  season: RangeSeason | null
  level?: number
}

/**
 * The group that holds this option, window and report season: its (low, high)
 * multipliers and what they scale. Falls back to the scenario over both battery
 * types, then to everything — within the season first, then over all seasons. A
 * scenario the table was never calibrated on gets none.
 */
function rangeGroup(
  table: RangeTable,
  params: RangeLookup
): { bounds: [number, number]; basis: RangeBasis } | null {
  const { batteryType, scenarioId, weightA, weightI, season, level = SHOWN_RANGE_LEVEL } = params
  const index = indexOf(table)
  if (!index.scenarios.has(scenarioId)) return null
  const bin = rangeBin(weightA, weightI, table.edges)
  const levelKey = String(level)
  for (const s of season ? [season, ANY] : [ANY]) {
    for (const [t, sc] of [
      [batteryType, scenarioId],
      [ANY, scenarioId],
      [ANY, ANY],
    ] as const) {
      const group = index.groups.get(groupKey(t, sc, s, bin))
      const bounds = group?.ranges[levelKey]
      if (group && bounds) return { bounds, basis: group.basis }
    }
  }
  return null
}

/** The (low, high) multipliers for this option, window and report season (see rangeGroup). */
export function rangeMultipliers(table: RangeTable, params: RangeLookup): [number, number] | null {
  return rangeGroup(table, params)?.bounds ?? null
}

export interface SavingsRange {
  level: number
  lowEur: number
  highEur: number
  /** Payback at the high end of the savings (fastest) and at the low end (slowest). */
  paybackLowYears: number
  paybackHighYears: number
}

/**
 * The calibrated range around one option's annual savings, with the payback range
 * that goes with it. Null when the estimate is too small to scale, or the table has
 * no range for this kind of window (e.g. a measured full year, which needs none).
 */
export function savingsRange(
  row: Pick<CapacitySweepRow, 'annualSavingsEur' | 'annualGrossEur' | 'standbyEur' | 'costEur'>,
  params: {
    batteryType: string
    scenarioId: string
    weightA: number
    weightI: number
    reportDate: Date
    level?: number
    table?: RangeTable
  }
): SavingsRange | null {
  const { table = BATTERY_RANGES, level = SHOWN_RANGE_LEVEL, reportDate, ...option } = params
  if (row.annualSavingsEur < table.min_savings_eur) return null
  const group = rangeGroup(table, { ...option, season: rangeSeason(reportDate), level })
  if (!group) return null
  // Savings never go below zero, as in the sweep: standby can eat a gross figure whole.
  const scale = (multiplier: number) =>
    group.basis === 'gross'
      ? Math.max(0, row.annualGrossEur * multiplier - row.standbyEur)
      : row.annualSavingsEur * multiplier
  const lowEur = scale(group.bounds[0])
  const highEur = scale(group.bounds[1])
  return {
    level,
    lowEur,
    highEur,
    paybackLowYears: paybackYearsWithDegradation(row.costEur, highEur),
    paybackHighYears: paybackYearsWithDegradation(row.costEur, lowEur),
  }
}

/** The level whose middle the report shows when the estimate falls outside its range. */
const MIDDLE_LEVEL = 0.5

/** One option as the report shows it. */
export interface RangedRow {
  /** The row as shown: the estimate, or the middle of its range (see `adjusted`). */
  row: CapacitySweepRow
  /** The range around the estimate, worked out from the estimate itself. */
  range: SavingsRange | null
  /** Whether the shown savings moved to the middle of the range, and which way. */
  adjusted: 'up' | 'down' | null
}

/**
 * One option as the report shows it. Where the calibration found the estimate off
 * in the same direction for nearly every household in this situation, its range
 * lies wholly above or below it: today that is zelfverbruik from about one winter
 * month, which shows almost no feed-in and so fills in too little summer surplus.
 * The shown savings then move to the middle of the 50% range, payback and ROI with
 * them; the range itself still comes from the estimate, so it stays where it was.
 */
export function rangedRow(
  row: CapacitySweepRow,
  params: Parameters<typeof savingsRange>[1]
): RangedRow {
  const range = savingsRange(row, params)
  if (!range) return { row, range, adjusted: null }
  const adjusted =
    row.annualSavingsEur < range.lowEur ? 'up' : row.annualSavingsEur > range.highEur ? 'down' : null
  const middle = adjusted ? savingsRange(row, { ...params, level: MIDDLE_LEVEL }) : null
  if (!adjusted || !middle) return { row, range, adjusted: null }
  const annualSavingsEur = (middle.lowEur + middle.highEur) / 2
  return {
    row: {
      ...row,
      annualSavingsEur,
      annualGrossEur: annualSavingsEur + row.standbyEur,
      paybackYears: paybackYearsWithDegradation(row.costEur, annualSavingsEur),
      roiPercent: row.costEur > 0 ? (annualSavingsEur / row.costEur) * 100 : 0,
    },
    range,
    adjusted,
  }
}
